import { t } from 'i18next';

import * as v from 'valibot';
import { IdSchema } from './common';
import moment from 'moment';

export const TaskFrequency = v.picklist([ 'daily', 'weekly', 'workDays', 'custom' ]);
export const TaskRepeatNotification = v.picklist([ '5min', '30min', '1hour' ]);
export const TaskCustomFrequencyName = v.picklist([ 'weeks', 'months' ]);

export const TaskFormSchema = v.object({
	name: v.pipe(v.string(), v.nonEmpty(t('Prosím nenechávejte toto pole prázdné.'))),
	description: v.nullable(v.pipe(v.string(), v.nonEmpty(t('Prosím nenechávejte toto pole prázdné.')))),
	roleId: v.pipe(v.array(IdSchema), v.nonEmpty(t('Prosím zvolte roli uživatele.'))),
	startDate: v.nullable(v.pipe(v.string(), v.isoDate())),
	dueDate: v.nullable(v.pipe(v.string(), v.isoDate())),
	taskTypeId: v.pipe(
		v.nullable(IdSchema),
		v.check((value) => value !== null, t('Prosím, zvolte typ úkolu.')),
	),
	taskTypeStatusId: v.pipe(
		v.nullable(IdSchema),
		v.check((value) => value !== null, t('Prosím, zvolte stav úkolu.')),
	),
	frequency: v.nullable(TaskFrequency),
	customFrequency: v.nullable(v.object({
		days: v.array(v.number()),
		interval: v.pipe(
			v.number(t('Prosím, zadejte platné číslo.')),
			v.integer(),
		),
		name: TaskCustomFrequencyName,
	})),
	taskRepeat: v.nullable(v.object({
		auditId: v.pipe(
			v.nullable(IdSchema),
			v.check((value) => value !== null, t('Prosím, zvolte audit.')),
		),
		notification: v.nullable(TaskRepeatNotification),
		productCategoryId: v.pipe(
			v.nullable(IdSchema),
			v.check((value) => value !== null, t('Prosím, zvolte kategorii.')),
		),
		productTypeId: v.nullable(IdSchema),
	})),
});

export type TaskFormModel = v.InferInput<typeof TaskFormSchema>;

export const ReccuringTaskFormSchema = v.pipe(
	v.object({
		...TaskFormSchema.entries,
		startDate: v.pipe(v.string(), v.isoDate()),
		dueDate: v.pipe(v.string(), v.isoDate()),
		startTime: v.pipe(v.string(), v.isoTime()),
		dueTime: v.pipe(v.string(), v.isoTime()),
		frequency: TaskFrequency,
		taskRepeat: v.object({
			auditId: v.pipe(
				v.nullable(IdSchema),
				v.check((value) => value !== null, t('Prosím, zvolte audit.')),
			),
			notification: v.nullable(TaskRepeatNotification),
			productCategoryId: v.pipe(
				v.nullable(IdSchema),
				v.check((value) => value !== null, t('Prosím, zvolte kategorii.')),
			),
			productTypeId: v.nullable(IdSchema),
		}),
	}),
	v.forward(
		v.partialCheck(
			[ [ 'startDate' ], [ 'startTime' ], [ 'dueDate' ], [ 'dueTime' ] ],
			({ startDate, startTime, dueDate, dueTime }) => {
				const start = moment(`${ startDate }T${ startTime }`, true);
				const due = moment(`${ dueDate }T${ dueTime }`, true);

				return start.isBefore(due);
			},
			t('Prosím, zvolte časové údaje tak, aby začátek byl před koncem.'),
		),
		[ 'startDate' ],
	),
	v.forward(
		v.partialCheck(
			[ [ 'frequency' ], [ 'customFrequency' ] ],
			({ frequency, customFrequency }) => frequency !== 'custom' || customFrequency != null,
			t('Prosím, vyplňte interval pro vlastní frekvenci.'),
		),
		[ 'customFrequency' ],
	),
	v.forward(
		v.partialCheck(
			[ [ 'frequency' ], [ 'customFrequency' ] ],
			({ frequency, customFrequency }) => {
				if (frequency !== 'custom') { return true; }
				if (customFrequency == null) { return false; }

				const { name, interval } = customFrequency;

				if (name === 'weeks') { return interval >= 1 && interval <= 99; }
				if (name === 'months') { return interval >= 1 && interval <= 3; }

				return true;
			},
			({ input: { customFrequency } }) =>
				(customFrequency === null
					? t('Prosím, vyplňte interval pro vlastní frekvenci.')
					: customFrequency.name === 'weeks'
						? t('Prosím, zadejte hodnotu od %min% do %max%.', { min: 1, max: 99 })
						: t('Prosím, zadejte hodnotu od %min% do %max%.', { min: 1, max: 3 })),

		),
		[ 'customFrequency', 'interval' ],
	),
);

export type ReccuringTaskFormModel = v.InferInput<typeof ReccuringTaskFormSchema>;

